import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, BookOpen, Clock, AlertTriangle, Target, Lightbulb, CheckCircle } from 'lucide-react';
import englishDataService from '../services/englishDataService';
import { chatContexts } from '../data/chatData';
import FloatingChatWindow from './FloatingChatWindow';
import ChatWithAIButton from './ChatWithAIButton';
import ESSToolboxEnhanced from './ESSToolboxEnhanced';
import LinguisticAnalysis from './LinguisticAnalysis';
import { useUISettings } from './UISettingsContext';
import LoadingSpinner from './LoadingSpinner';

const HKDSEGrammar = () => {
  const [activeCategory, setActiveCategory] = useState('parts-of-speech');
  const [expandedSections, setExpandedSections] = useState({});
  const [currentExercise, setCurrentExercise] = useState(null);
  const [exerciseAnswers, setExerciseAnswers] = useState({});
  const [showAnswers, setShowAnswers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatContext, setChatContext] = useState({});
  const [savedQuizlets, setSavedQuizlets] = useState([]);
  const [savedDrills, setSavedDrills] = useState([]);
  const [selectedAnalysisExample, setSelectedAnalysisExample] = useState(null);
  const { tabLabelMode } = useUISettings();

  // Supabase data states
  const [grammarCategories, setGrammarCategories] = useState({});
  const [partsOfSpeechData, setPartsOfSpeechData] = useState({});
  const [verbTensesData, setVerbTensesData] = useState({});
  const [sentenceStructureData, setSentenceStructureData] = useState({});
  const [clausesPhrasesData, setClausesPhrasesData] = useState({});
  const [commonErrorsData, setCommonErrorsData] = useState({});
  const [practiceExercisesData, setPracticeExercisesData] = useState({});
  const [hkdseExamTips, setHkdseExamTips] = useState({});
  const [linguisticAnalysisExamples, setLinguisticAnalysisExamples] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);

  // Load data from A2A backend on component mount
  useEffect(() => {
    const loadGrammarData = async () => {
      try {
        setDataLoading(true);
        setDataError(null);
        
        const [
          categories,
          partsOfSpeech,
          verbTenses,
          sentenceStructure,
          clausesPhrases,
          commonErrors,
          practiceExercises,
          examTips,
          linguisticExamples
        ] = await Promise.all([
          englishDataService.getGrammarCategories(),
          englishDataService.getPartsOfSpeechData(),
          englishDataService.getVerbTensesData(),
          englishDataService.getSentenceStructureData(),
          englishDataService.getClausesPhrasesData().catch(() => ({})), // May not have A2A capability
          englishDataService.getCommonErrorsData().catch(() => ({})), // May not have A2A capability
          englishDataService.getPracticeExercisesData().catch(() => ({})), // May not have A2A capability
          englishDataService.getHKDSEExamTips(),
          englishDataService.getLinguisticExamples({ limit: 100 })
        ]);
        
        setGrammarCategories(categories);
        setPartsOfSpeechData(partsOfSpeech);
        setVerbTensesData(verbTenses);
        setSentenceStructureData(sentenceStructure);
        setClausesPhrasesData(clausesPhrases);
        setCommonErrorsData(commonErrors);
        setPracticeExercisesData(practiceExercises);
        setHkdseExamTips(examTips);
        // Handle linguistic examples format
        const examplesArray = Array.isArray(linguisticExamples) ? linguisticExamples : (linguisticExamples.data || linguisticExamples.examples || []);
        setLinguisticAnalysisExamples(examplesArray);
        
        // Set the first linguistic analysis example as selected
        if (examplesArray.length > 0) {
          setSelectedAnalysisExample(examplesArray[0]);
        }
      } catch (error) {
        console.error('Error loading grammar data:', error);
        setDataError(error.message);
      } finally {
        setDataLoading(false);
      }
    };

    loadGrammarData();
  }, []);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleExerciseAnswer = (questionId, answer) => {
    setExerciseAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  // Chat functions
  const handleOpenChat = (context) => {
    setChatContext(context);
    setIsChatOpen(true);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
    setChatContext({});
  };

  const handleSaveAsQuizlet = (quizletData) => {
    setSavedQuizlets(prev => [...prev, { ...quizletData, id: Date.now() }]);
    // Here you would typically save to your backend/Supabase
    console.log('Saved as Quizlet:', quizletData);
  };

  const handleSaveAsDrill = (drillData) => {
    setSavedDrills(prev => [...prev, { ...drillData, id: Date.now() }]);
    // Here you would typically save to your backend/Supabase
    console.log('Saved as Drill:', drillData);
  };

  const handleReturnToTopic = () => {
    // Scroll to relevant section or highlight topic
    console.log('Returning to topic:', chatContext);
  };

  // Get chat context for current topic
  const getChatContext = (categoryId, topicId, subtopicId) => {
    const category = chatContexts[categoryId];
    if (!category) return {};
    
    const topic = category.topics?.[topicId];
    if (!topic) return { title: category.title, id: categoryId };
    
    const subtopic = topic.subtopics?.[subtopicId];
    if (!subtopic) return { title: topic.title, id: topicId };
    
    return {
      title: subtopic.title,
      id: subtopic.id,
      context: subtopic.context,
      keywords: subtopic.keywords
    };
  };

  // Helper function to render examples based on different data structures
  const renderExamples = (examples) => {
    if (!examples) return null;
    
    if (Array.isArray(examples)) {
      return (
        <div className="flex flex-wrap gap-2">
          {examples.map((example, i) => (
            <span key={i} className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
              {example}
            </span>
          ))}
        </div>
      );
    }
    
    if (typeof examples === 'object') {
      return (
        <div className="space-y-2">
          {Object.entries(examples).map(([key, values]) => (
            <div key={key}>
              <div className="text-xs font-medium text-gray-600 mb-1 capitalize">{key}:</div>
              <div className="flex flex-wrap gap-2">
                {Array.isArray(values) ? values.map((example, i) => (
                  <span key={i} className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                    {example}
                  </span>
                )) : null}
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    return null;
  };

  const TenseTimeline = ({ tenses }) => (
    <div className="my-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
      <h4 className="text-lg font-semibold mb-4 text-center">📅 Tense Timeline (時態時間軸)</h4>
      <div className="flex justify-between items-center">
        <div className="text-center">
          <div className="text-sm font-medium text-gray-600 mb-2">PAST</div>
          <div className="space-y-1">
            {verbTensesData.timeline.past.map(tense => (
              <div key={tense} className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                {tense}
              </div>
            ))}
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm font-medium text-gray-600 mb-2">PRESENT</div>
          <div className="space-y-1">
            {verbTensesData.timeline.present.map(tense => (
              <div key={tense} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                {tense}
              </div>
            ))}
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm font-medium text-gray-600 mb-2">FUTURE</div>
          <div className="space-y-1">
            {verbTensesData.timeline.future.map(tense => (
              <div key={tense} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                {tense}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const SentenceStructureDiagram = ({ pattern, examples }) => (
    <div className="my-4 p-4 bg-gray-50 rounded-lg">
      <div className="text-center mb-3">
        <div className="text-lg font-mono font-bold text-blue-600">{pattern}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {examples.map((example, index) => (
          <div key={index} className="text-center p-2 bg-white rounded border">
            <div className="text-sm">{example}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const GrammarCard = ({ title, content, color, icon }) => (
    <div className={`p-4 rounded-lg border-l-4 ${color} bg-white shadow-sm`}>
      <div className="flex items-center mb-2">
        <span className="text-lg mr-2">{icon}</span>
        <h4 className="font-semibold text-gray-800">{title}</h4>
      </div>
      <div className="text-sm text-gray-600">{content}</div>
    </div>
  );

  const ExerciseQuestion = ({ question, questionId, type }) => (
    <div className="p-4 bg-gray-50 rounded-lg mb-4">
      <div className="mb-3">
        <div className="text-sm font-medium text-gray-700 mb-2">{question.sentence}</div>
        {type === 'completion' && question.options && (
          <div className="grid grid-cols-2 gap-2">
            {question.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleExerciseAnswer(questionId, option)}
                className={`p-2 text-left rounded border ${
                  exerciseAnswers[questionId] === option
                    ? 'bg-blue-100 border-blue-300'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>
      {showAnswers && (
        <div className="mt-3 p-3 bg-green-50 rounded border-l-4 border-green-400">
          <div className="text-sm font-medium text-green-800">
            ✓ Correct Answer: {question.correctAnswer}
          </div>
          <div className="text-sm text-green-700 mt-1">
            {question.explanation}
          </div>
        </div>
      )}
    </div>
  );

  const renderPartsOfSpeech = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-800 mb-2">
          🏷️ 詞性分析 (Parts of Speech)
        </h3>
        <p className="text-gray-600">Complete analysis of all word types in English</p>
      </div>

      {Object.entries(partsOfSpeechData || {}).map(([key, data]) => (
        <div key={key} className="bg-white rounded-lg shadow-md overflow-hidden">
          <button
            onClick={() => toggleSection(key)}
            className="w-full p-4 text-left bg-blue-50 hover:bg-blue-100 transition-colors flex items-center justify-between"
          >
            <div className="flex items-center">
              <span className="text-2xl mr-3">
                {key === 'nouns' ? '🏠' : key === 'pronouns' ? '👤' : key === 'verbs' ? '🏃' : 
                 key === 'adjectives' ? '🎨' : '⚡'}
              </span>
              <div>
                <h4 className="font-semibold text-gray-800">{data.title}</h4>
                <p className="text-sm text-gray-600">{data.definition}</p>
              </div>
            </div>
            {expandedSections[key] ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </button>
          
          {expandedSections[key] && (
            <div className="p-4 space-y-4">
              {data.types?.map((type, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold text-blue-600">{type.name}</h5>
                    <ChatWithAIButton
                      onClick={handleOpenChat}
                      context={getChatContext('parts-of-speech', key, type.type)}
                      variant="secondary"
                      size="small"
                    />
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{type.definition}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h6 className="font-medium text-gray-700 mb-2">Examples:</h6>
                      {renderExamples(type.examples)}
                    </div>
                    
                    <div>
                      <h6 className="font-medium text-gray-700 mb-2">Rules:</h6>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {type.rules?.map((rule, i) => (
                          <li key={i} className="flex items-start">
                            <span className="text-blue-500 mr-2">•</span>
                            {rule}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="mt-3 p-3 bg-yellow-50 rounded border-l-4 border-yellow-400">
                    <div className="text-sm font-medium text-yellow-800">HKDSE Note:</div>
                    <div className="text-sm text-yellow-700">{type.hkdseNote}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderVerbTenses = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-800 mb-2">
          ⏰ 動詞時態 (Verb Tenses)
        </h3>
        <p className="text-gray-600">12 major tenses with visual timeline and examples</p>
      </div>

      <TenseTimeline tenses={verbTensesData.timeline} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {verbTensesData.tenses?.map((tense, index) => (
          <div key={tense.id} className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-bold text-lg">{tense.name}</h4>
                  <p className="text-sm opacity-90">{tense.chineseName}</p>
                </div>
                <ChatWithAIButton
                  onClick={handleOpenChat}
                  context={getChatContext('verb-tenses', 'present', tense.id)}
                  variant="secondary"
                  size="small"
                />
              </div>
            </div>
            
            <div className="p-4 space-y-3">
              <div>
                <h5 className="font-semibold text-gray-700 mb-1">Structure:</h5>
                <div className="bg-gray-100 p-2 rounded text-sm font-mono">
                  {tense.structure}
                </div>
              </div>
              
              <div>
                <h5 className="font-semibold text-gray-700 mb-1">Examples:</h5>
                <ul className="text-sm space-y-1">
                  {tense.examples.map((example, i) => (
                    <li key={i} className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      {example}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h5 className="font-semibold text-gray-700 mb-1">Usage:</h5>
                <ul className="text-sm space-y-1">
                  {tense.usage.map((use, i) => (
                    <li key={i} className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      {use}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h5 className="font-semibold text-gray-700 mb-1">Time Markers:</h5>
                <div className="flex flex-wrap gap-1">
                  {tense.timeMarkers.map((marker, i) => (
                    <span key={i} className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">
                      {marker}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="mt-3 p-3 bg-yellow-50 rounded border-l-4 border-yellow-400">
                <div className="text-sm font-medium text-yellow-800">HKDSE Note:</div>
                <div className="text-sm text-yellow-700">{tense.hkdseNote}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSentenceStructure = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-800 mb-2">
          🏗️ 句子結構 (Sentence Structure)
        </h3>
        <p className="text-gray-600">Five basic sentence patterns and complex structures</p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h4 className="text-xl font-semibold mb-4">Basic Sentence Patterns</h4>
        <div className="space-y-4">
          {sentenceStructureData.basicPatterns.map((pattern, index) => (
            <div key={pattern.id} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h5 className="font-semibold text-blue-600">{pattern.name}</h5>
                  <p className="text-sm text-gray-600">{pattern.chineseName}</p>
                </div>
                <div className="text-lg font-mono font-bold text-purple-600">
                  {pattern.pattern}
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-3">{pattern.description}</p>
              
              <SentenceStructureDiagram 
                pattern={pattern.pattern} 
                examples={pattern.examples} 
              />
              
              <div className="mt-3 p-3 bg-blue-50 rounded border-l-4 border-blue-400">
                <div className="text-sm font-medium text-blue-800">HKDSE Note:</div>
                <div className="text-sm text-blue-700">{pattern.hkdseNote}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h4 className="text-xl font-semibold mb-4">Sentence Types</h4>
          <div className="space-y-4">
            {sentenceStructureData.sentenceTypes.map((type, index) => (
              <div key={type.id} className="border rounded-lg p-4">
                <h5 className="font-semibold text-green-600 mb-2">{type.name}</h5>
                <p className="text-sm text-gray-600 mb-2">{type.chineseName}</p>
                <p className="text-sm text-gray-600 mb-3">{type.definition}</p>
                
                <div className="bg-gray-100 p-2 rounded text-sm font-mono mb-3">
                  {type.structure}
                </div>
                
                <div className="space-y-2">
                  {type.examples.map((example, i) => (
                    <div key={i} className="text-sm bg-green-50 p-2 rounded">
                      {example}
                    </div>
                  ))}
                </div>
                
                {type.connectors && (
                  <div className="mt-3">
                    <h6 className="font-medium text-gray-700 mb-1">Connectors:</h6>
                    <div className="flex flex-wrap gap-1">
                      {type.connectors.map((connector, i) => (
                        <span key={i} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                          {connector}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="mt-3 p-3 bg-yellow-50 rounded border-l-4 border-yellow-400">
                  <div className="text-sm font-medium text-yellow-800">HKDSE Note:</div>
                  <div className="text-sm text-yellow-700">{type.hkdseNote}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h4 className="text-xl font-semibold mb-4">Voice Types</h4>
          <div className="space-y-4">
            {sentenceStructureData.voiceTypes.map((voice, index) => (
              <div key={voice.id} className="border rounded-lg p-4">
                <h5 className="font-semibold text-purple-600 mb-2">{voice.name}</h5>
                <p className="text-sm text-gray-600 mb-2">{voice.chineseName}</p>
                <p className="text-sm text-gray-600 mb-3">{voice.definition}</p>
                
                <div className="bg-gray-100 p-2 rounded text-sm font-mono mb-3">
                  {voice.structure}
                </div>
                
                <div className="space-y-2 mb-3">
                  {voice.examples.map((example, i) => (
                    <div key={i} className="text-sm bg-purple-50 p-2 rounded">
                      {example}
                    </div>
                  ))}
                </div>
                
                {voice.advantages && (
                  <div className="mb-3">
                    <h6 className="font-medium text-gray-700 mb-1">Advantages:</h6>
                    <ul className="text-sm space-y-1">
                      {voice.advantages.map((advantage, i) => (
                        <li key={i} className="flex items-start">
                          <span className="text-green-500 mr-2">•</span>
                          {advantage}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {voice.uses && (
                  <div className="mb-3">
                    <h6 className="font-medium text-gray-700 mb-1">Uses:</h6>
                    <ul className="text-sm space-y-1">
                      {voice.uses.map((use, i) => (
                        <li key={i} className="flex items-start">
                          <span className="text-blue-500 mr-2">•</span>
                          {use}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="mt-3 p-3 bg-yellow-50 rounded border-l-4 border-yellow-400">
                  <div className="text-sm font-medium text-yellow-800">HKDSE Note:</div>
                  <div className="text-sm text-yellow-700">{voice.hkdseNote}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderClausesPhrases = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-800 mb-2">
          🔗 子句與片語 (Clauses & Phrases)
        </h3>
        <p className="text-gray-600">Independent, dependent clauses and phrase types</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h4 className="text-xl font-semibold mb-4 flex items-center">
            <span className="text-2xl mr-2">🔗</span>
            Clauses (子句)
          </h4>
          <div className="space-y-4">
            {clausesPhrasesData.clauses?.map((clause, index) => (
              <div key={clause.id} className="border rounded-lg p-4">
                <h5 className="font-semibold text-blue-600 mb-2">{clause.name}</h5>
                <p className="text-sm text-gray-600 mb-2">{clause.chineseName}</p>
                <p className="text-sm text-gray-600 mb-3">{clause.definition}</p>
                
                <div className="bg-gray-100 p-2 rounded text-sm font-mono mb-3">
                  {clause.structure}
                </div>
                
                <div className="space-y-2 mb-3">
                  {clause.examples?.map((example, i) => (
                    <div key={i} className="text-sm bg-blue-50 p-2 rounded">
                      {example}
                    </div>
                  ))}
                </div>
                
                {clause.characteristics && (
                  <div className="mb-3">
                    <h6 className="font-medium text-gray-700 mb-1">Characteristics:</h6>
                    <ul className="text-sm space-y-1">
                      {clause.characteristics?.map((char, i) => (
                        <li key={i} className="flex items-start">
                          <span className="text-green-500 mr-2">•</span>
                          {char}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {clause.types && (
                  <div className="space-y-3">
                    {clause.types.map((type, i) => (
                      typeof type === 'string' ? (
                        <div key={i} className="bg-gray-50 p-3 rounded">
                          <h6 className="font-medium text-purple-600 mb-1">{type}</h6>
                        </div>
                      ) : (
                        <div key={i} className="bg-gray-50 p-3 rounded">
                          <h6 className="font-medium text-purple-600 mb-1">{type.name || type.type || JSON.stringify(type)}</h6>
                          {type.chineseName && <p className="text-sm text-gray-600 mb-2">{type.chineseName}</p>}
                          {type.definition && <p className="text-sm text-gray-600 mb-2">{type.definition}</p>}
                          {type.examples && (
                            <div className="space-y-1">
                              {type.examples.map((example, j) => (
                                <div key={j} className="text-sm bg-white p-2 rounded">
                                  {example}
                                </div>
                              ))}
                            </div>
                          )}
                          {type.hkdseNote && (
                            <div className="mt-2 p-2 bg-yellow-50 rounded border-l-4 border-yellow-400">
                              <div className="text-sm font-medium text-yellow-800">HKDSE Note:</div>
                              <div className="text-sm text-yellow-700">{type.hkdseNote}</div>
                            </div>
                          )}
                        </div>
                      )
                    ))}
                  </div>
                )}
                
                <div className="mt-3 p-3 bg-yellow-50 rounded border-l-4 border-yellow-400">
                  <div className="text-sm font-medium text-yellow-800">HKDSE Note:</div>
                  <div className="text-sm text-yellow-700">{clause.hkdseNote}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h4 className="text-xl font-semibold mb-4 flex items-center">
            <span className="text-2xl mr-2">📝</span>
            Phrases (片語)
          </h4>
          <div className="space-y-4">
            {clausesPhrasesData.phrases?.map((phrase, index) => (
              <div key={phrase.id} className="border rounded-lg p-4">
                <h5 className="font-semibold text-green-600 mb-2">{phrase.name}</h5>
                <p className="text-sm text-gray-600 mb-2">{phrase.chineseName}</p>
                <p className="text-sm text-gray-600 mb-3">{phrase.definition}</p>
                
                <div className="bg-gray-100 p-2 rounded text-sm font-mono mb-3">
                  {phrase.structure}
                </div>
                
                <div className="space-y-2 mb-3">
                  {phrase.examples?.map((example, i) => (
                    <div key={i} className="text-sm bg-green-50 p-2 rounded">
                      {example}
                    </div>
                  ))}
                </div>
                
                {phrase.types && (
                  <div className="space-y-3">
                    {phrase.types?.map((type, i) => (
                      <div key={i} className="bg-gray-50 p-3 rounded">
                        <h6 className="font-medium text-orange-600 mb-1">{type.name}</h6>
                        <div className="space-y-1">
                          {type.examples?.map((example, j) => (
                            <div key={j} className="text-sm bg-white p-2 rounded">
                              {example}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {phrase.functions && (
                  <div className="mb-3">
                    <h6 className="font-medium text-gray-700 mb-1">Functions:</h6>
                    <div className="flex flex-wrap gap-1">
                      {phrase.functions?.map((func, i) => (
                        <span key={i} className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">
                          {typeof func === 'string' ? func : func.name || func.type || func.function || JSON.stringify(func)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="mt-3 p-3 bg-yellow-50 rounded border-l-4 border-yellow-400">
                  <div className="text-sm font-medium text-yellow-800">HKDSE Note:</div>
                  <div className="text-sm text-yellow-700">{phrase.hkdseNote}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderCommonErrors = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-800 mb-2">
          ⚠️ 常見錯誤 (Common Errors)
        </h3>
        <p className="text-gray-600">Typical mistakes and how to avoid them</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Object.entries(commonErrorsData).map(([key, errorType]) => (
          <div key={key} className="bg-white rounded-lg shadow-md p-6">
            <h4 className="text-xl font-semibold mb-4 flex items-center">
              <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
              {errorType.title}
            </h4>
            <p className="text-gray-600 mb-4">{errorType.description}</p>
            
            {errorType.rules && (
              <div className="space-y-4">
                {errorType.rules.map((rule, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <h5 className="font-semibold text-red-600 mb-2">{rule.rule}</h5>
                    <div className="space-y-2">
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />
                        <div>
                          <span className="text-sm font-medium text-green-700">Correct:</span>
                          <div className="text-sm text-gray-700">{rule.correct}</div>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <AlertTriangle className="w-4 h-4 text-red-500 mr-2 mt-0.5" />
                        <div>
                          <span className="text-sm font-medium text-red-700">Incorrect:</span>
                          <div className="text-sm text-gray-700">{rule.incorrect}</div>
                        </div>
                      </div>
                      <div className="text-sm text-blue-600 bg-blue-50 p-2 rounded">
                        <span className="font-medium">Explanation:</span> {rule.explanation}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {errorType.commonMistakes && (
              <div className="space-y-4">
                {errorType.commonMistakes.map((mistake, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <h5 className="font-semibold text-red-600 mb-2">{mistake.type}</h5>
                    <div className="space-y-2">
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />
                        <div>
                          <span className="text-sm font-medium text-green-700">Correct:</span>
                          <div className="text-sm text-gray-700">{mistake.correct}</div>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <AlertTriangle className="w-4 h-4 text-red-500 mr-2 mt-0.5" />
                        <div>
                          <span className="text-sm font-medium text-red-700">Incorrect:</span>
                          <div className="text-sm text-gray-700">{mistake.incorrect}</div>
                        </div>
                      </div>
                      <div className="text-sm text-blue-600 bg-blue-50 p-2 rounded">
                        <span className="font-medium">Rule:</span> {mistake.rule}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-4 p-3 bg-yellow-50 rounded border-l-4 border-yellow-400">
              <div className="text-sm font-medium text-yellow-800">HKDSE Note:</div>
              <div className="text-sm text-yellow-700">{errorType.hkdseNote}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPracticeExercises = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-800 mb-2">
          📝 綜合練習 (Practice Exercises)
        </h3>
        <p className="text-gray-600">Interactive exercises and practice questions</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.isArray(practiceExercisesData) ? practiceExercisesData.map((exercise) => (
          <div key={exercise.id} className="bg-white rounded-lg shadow-md p-6">
            <h4 className="text-lg font-semibold mb-4 flex items-center">
              <Target className="w-5 h-5 text-blue-500 mr-2" />
              {exercise.title || 'Practice Exercise'}
            </h4>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded">
                <p className="text-sm font-medium text-blue-800">{exercise.instruction}</p>
              </div>
              
              {exercise.questions && exercise.questions.map((question, qIndex) => (
                <ExerciseQuestion
                  key={qIndex}
                  question={question}
                  questionId={`${exercise.id}-${qIndex}`}
                  type={exercise.type}
                />
              ))}
              
              <div className="flex justify-between items-center">
                <button
                  onClick={() => setShowAnswers(!showAnswers)}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                >
                  {showAnswers ? 'Hide Answers' : 'Show Answers'}
                </button>
                
                <div className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded">
                  {exercise.hkdseRelevance}
                </div>
              </div>
            </div>
          </div>
        )) : (
          <div className="col-span-full text-center py-12">
            <div className="text-lg text-gray-600">No practice exercises available</div>
          </div>
        )}
      </div>
    </div>
  );

  const renderHKDSETips = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-800 mb-2">
          💡 HKDSE 應考貼士 (Exam Tips)
        </h3>
        <p className="text-gray-600">Strategic tips for each paper</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Object.entries(hkdseExamTips).map(([key, paper]) => (
          <div key={key} className="bg-white rounded-lg shadow-md p-6">
            <h4 className="text-xl font-semibold mb-4 flex items-center">
              <Lightbulb className="w-5 h-5 text-yellow-500 mr-2" />
              {paper.title}
            </h4>
            
            <div className="space-y-4">
              <div>
                <h5 className="font-semibold text-blue-600 mb-2">General Tips:</h5>
                <ul className="space-y-2">
                  {paper.tips.map((tip, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      <span className="text-sm text-gray-700">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h5 className="font-semibold text-purple-600 mb-2">Grammar Focus:</h5>
                <div className="flex flex-wrap gap-2">
                  {paper.grammarFocus.map((focus, index) => (
                    <span key={index} className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm">
                      {focus}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderLinguisticAnalysis = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
        <h2 className="text-2xl font-bold text-blue-800 mb-4 flex items-center gap-2">
          <BookOpen className="w-6 h-6" />
          Linguistic Analysis with Legend Structure
        </h2>
        <p className="text-gray-700 mb-6">
          Advanced sentence structure analysis with detailed breakdown and Legend Structure presentation.
        </p>
      </div>

      {/* Example Selector */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Select Analysis Example:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {linguisticAnalysisExamples.map((example) => (
            <button
              key={example.id}
              onClick={() => setSelectedAnalysisExample(example)}
              className={`p-4 rounded-lg border-2 transition-all ${
                selectedAnalysisExample.id === example.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-blue-25'
              }`}
            >
              <h4 className="font-medium text-gray-800 mb-2">{example.title}</h4>
              <p className="text-sm text-gray-600 line-clamp-2">{example.originalSentence}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Linguistic Analysis Display */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <LinguisticAnalysis 
          analysisData={selectedAnalysisExample.analysis} 
          originalSentence={selectedAnalysisExample.originalSentence}
        />
      </div>

      {/* Additional Features */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-yellow-600" />
            Analysis Features
          </h3>
          <ul className="space-y-2 text-gray-600">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              Main clause pattern identification
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              Legend structure visualization
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              Subordinate clause analysis
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              Verb tense identification
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              Rewrite options for different patterns
            </li>
          </ul>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-purple-600" />
            HKDSE Relevance
          </h3>
          <div className="space-y-3 text-gray-600">
            <p className="text-sm">
              <strong>Paper 1 (Reading):</strong> Understanding complex sentence structures in reading passages
            </p>
            <p className="text-sm">
              <strong>Paper 2 (Writing):</strong> Using varied sentence patterns for better writing
            </p>
            <p className="text-sm">
              <strong>Paper 3 (Listening):</strong> Recognizing sentence patterns in spoken English
            </p>
            <p className="text-sm">
              <strong>Paper 4 (Speaking):</strong> Using appropriate sentence structures in responses
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Show loading state while data is being fetched
  if (dataLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner isLoading={true} progress={0} currentStep="Loading grammar data..." />
          <p className="mt-4 text-gray-600">Loading HKDSE Grammar data from database...</p>
        </div>
      </div>
    );
  }

  // Show error state if data loading failed
  if (dataError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️ Error Loading Data</div>
          <p className="text-gray-600 mb-4">{dataError}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    // Basic data validation
    if (!grammarCategories || !partsOfSpeechData) {
      return (
        <div className="text-center py-12">
          <div className="text-lg text-gray-600">Loading grammar data...</div>
        </div>
      );
    }


    switch (activeCategory) {
      case 'parts-of-speech':
        return renderPartsOfSpeech();
      case 'verb-tenses':
        return renderVerbTenses();
      case 'sentence-structure':
        return renderSentenceStructure();
      case 'clauses-phrases':
        return renderClausesPhrases();
      case 'common-errors':
        return renderCommonErrors();
      case 'practice-exercises':
        return renderPracticeExercises();
      case 'hkdse-tips':
        return renderHKDSETips();
      case 'linguistic-analysis':
        return renderLinguisticAnalysis();
      case 'ess-toolbox':
        return <ESSToolboxEnhanced />;
      default:
        return renderPartsOfSpeech();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              📚 HKDSE English Grammar
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              Complete Grammar Learning System for HKDSE English Language
            </p>
            <div className="text-sm text-gray-500">
              Study for HKDSE English Language • Comprehensive Grammar Guide • Interactive Learning
            </div>
          </div>
        </div>
      </div>

      {/* Category Navigation */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex overflow-x-auto py-4 space-x-2">
            {Object.entries(grammarCategories || {}).map(([key, category]) => (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                  activeCategory === key
                    ? `${category.color} text-white`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span>{category.icon}</span>
                {tabLabelMode === 'icon-text' && <span>{category.title}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {renderContent()}
      </div>

      {/* Global Floating Chat Button */}
      <ChatWithAIButton
        onClick={handleOpenChat}
        context={{ title: 'HKDSE English Grammar', id: 'grammar-general' }}
        position="floating"
        variant="floating"
        size="large"
      />

      {/* Floating Chat Window */}
      <FloatingChatWindow
        isOpen={isChatOpen}
        onClose={handleCloseChat}
        context={chatContext}
        onSaveAsQuizlet={handleSaveAsQuizlet}
        onSaveAsDrill={handleSaveAsDrill}
        onReturnToTopic={handleReturnToTopic}
      />

      {/* Footer */}
      <div className="bg-gray-100 border-t">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="text-center text-sm text-gray-600">
            <p>🎯 Designed specifically for HKDSE English Language preparation</p>
            <p className="mt-1">Complete grammar coverage • Interactive exercises • Exam tips</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HKDSEGrammar; 